import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Modal, EmptyState } from '../components/UI';
import { getCategory, getAllCategoryKeys, getFamilyColor, getInitials, formatCurrency, formatDateShort, today, validateExpense } from '../utils/helpers';
import { calculateExpenseSplit, getMemberById, getFamilyForMember, getAllMembers } from '../utils/calculator';

const SPLIT_LABELS = { equal:'50/50', custom:'Custom', per_person:'Per Person', weighted:'Weighted', family1_only:'F1 Only', family2_only:'F2 Only' };

export default function Expenses() {
  const { expenses, families, addExpense, updateExpense, deleteExpense, showToast } = useApp();
  const [filters, setFilters] = useState({ category: 'all', family: 'all', search: '' });
  const [modal, setModal] = useState(null); // null | { expenseId? }
  const [splitType, setSplitType] = useState('equal');

  const allMembers = useMemo(() => getAllMembers(families), [families]);

  const filtered = useMemo(() => {
    let list = [...expenses];
    if (filters.category !== 'all') list = list.filter(e => e.category === filters.category);
    if (filters.family !== 'all') list = list.filter(e => getFamilyForMember(e.paidBy, families) === filters.family);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(e => e.description.toLowerCase().includes(q) || (e.notes?.toLowerCase().includes(q)));
    }
    return list.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [expenses, families, filters]);

  const totalAmount = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const existingExpense = modal?.expenseId ? expenses.find(e => e.id === modal.expenseId) : null;

  const openAdd  = () => { setSplitType('equal'); setModal({}); };
  const openEdit = (id) => {
    const exp = expenses.find(e => e.id === id);
    setSplitType(exp?.splitType || 'equal');
    setModal({ expenseId: id });
  };
  const closeModal = () => setModal(null);

  const handleDelete = (exp) => {
    if (window.confirm(`Delete "${exp.description}"?`)) {
      deleteExpense(exp.id);
      showToast('Expense deleted', 'info');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = {
      date:         fd.get('date'),
      category:     fd.get('category'),
      description:  fd.get('description'),
      amount:       parseFloat(fd.get('amount')),
      paidBy:       fd.get('paidBy'),
      splitType:    fd.get('splitType'),
      family1Share: parseFloat(fd.get('family1Share')) || 0,
      family2Share: parseFloat(fd.get('family2Share')) || 0,
      notes:        fd.get('notes') || '',
      memberWeights: {},
    };
    if (data.splitType === 'weighted') {
      allMembers.forEach(m => {
        const w = fd.get(`weight_${m.id}`);
        data.memberWeights[m.id] = parseFloat(w) || 0;
      });
    }
    const errors = validateExpense(data);
    if (errors.length > 0) { showToast(errors[0], 'error'); return; }
    if (existingExpense) {
      await updateExpense(existingExpense.id, data);
      showToast('Expense updated', 'success');
    } else {
      await addExpense(data);
      showToast('Expense added', 'success');
    }
    closeModal();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Expenses</h1>
          <p className="page-subtitle">{expenses.length} expenses · Total: {formatCurrency(totalAmount)}</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Expense
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar glass-card">
        <div className="filter-group">
          <input type="text" className="form-input filter-search" placeholder="Search expenses..."
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
        </div>
        <div className="filter-group">
          <select className="form-select" value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}>
            <option value="all">All Categories</option>
            {getAllCategoryKeys().map(c => { const cat = getCategory(c); return <option key={c} value={c}>{cat.icon} {cat.label}</option>; })}
          </select>
        </div>
        <div className="filter-group">
          <select className="form-select" value={filters.family} onChange={e => setFilters(f => ({ ...f, family: e.target.value }))}>
            <option value="all">All Families</option>
            <option value="family1">{families.family1?.name || 'Family 1'}</option>
            <option value="family2">{families.family2?.name || 'Family 2'}</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="expense-list">
        {filtered.length === 0 ? (
          <EmptyState icon="📝" title="No expenses yet" subtitle="Start recording your trip expenses"
            action={<button className="btn btn-primary" onClick={openAdd}>Add First Expense</button>} />
        ) : (
          filtered.map(exp => {
            const cat    = getCategory(exp.category);
            const member = getMemberById(exp.paidBy, families);
            const famId  = getFamilyForMember(exp.paidBy, families);
            const colors = getFamilyColor(famId);
            return (
              <div className="expense-row glass-card" key={exp.id}>
                <div className="expense-cat-icon" style={{ background: `${cat.color}20`, color: cat.color }}>{cat.icon}</div>
                <div className="expense-main">
                  <div className="expense-desc">{exp.description}</div>
                  <div className="expense-meta">
                    <span className="category-pill" style={{ background: `${cat.color}20`, color: cat.color, border: `1px solid ${cat.color}40` }}>{cat.label}</span>
                    <span className="expense-date">{formatDateShort(exp.date)}</span>
                    <span className="split-badge">{SPLIT_LABELS[exp.splitType] || '50/50'}</span>
                  </div>
                </div>
                <div className="expense-payer">
                  <div className="payer-avatar-sm" style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                    {member ? getInitials(member.name) : '?'}
                  </div>
                  <span className="payer-name">{member?.name || 'Unknown'}</span>
                </div>
                <div className="expense-amount">{formatCurrency(exp.amount)}</div>
                <div className="expense-actions">
                  <button className="btn-icon" title="Edit" onClick={() => openEdit(exp.id)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button className="btn-icon" title="Delete" onClick={() => handleDelete(exp)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={!!modal} onClose={closeModal} title={existingExpense ? 'Edit Expense' : 'New Expense'} large>
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', maxHeight: '70vh' }}>
          <div className="form-row">
            <div className="form-group flex-1">
              <label className="form-label">Date</label>
              <input name="date" type="date" className="form-input" defaultValue={existingExpense?.date || today()} required />
            </div>
            <div className="form-group flex-1">
              <label className="form-label">Category</label>
              <select name="category" className="form-select" defaultValue={existingExpense?.category || 'food'} required>
                {getAllCategoryKeys().map(c => { const cat = getCategory(c); return <option key={c} value={c}>{cat.icon} {cat.label}</option>; })}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input name="description" type="text" className="form-input" defaultValue={existingExpense?.description || ''} required placeholder="What was this expense for?" />
          </div>
          <div className="form-row">
            <div className="form-group flex-1">
              <label className="form-label">Amount (₹)</label>
              <input name="amount" type="number" className="form-input" defaultValue={existingExpense?.amount || ''} required min="0.01" step="0.01" placeholder="0.00" />
            </div>
            <div className="form-group flex-1">
              <label className="form-label">Paid By</label>
              <select name="paidBy" className="form-select" defaultValue={existingExpense?.paidBy || ''} required>
                <option value="">Select member</option>
                {['family1', 'family2'].map(fid => {
                  const fam = families[fid];
                  return fam ? (
                    <optgroup key={fid} label={fam.name}>
                      {fam.members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </optgroup>
                  ) : null;
                })}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Split Type</label>
            <div className="split-options">
              {Object.entries(SPLIT_LABELS).map(([val, label]) => (
                <label key={val} className={`split-option${splitType === val ? ' active' : ''}`}>
                  <input type="radio" name="splitType" value={val} checked={splitType === val}
                    onChange={() => setSplitType(val)} /> {label}
                </label>
              ))}
            </div>
          </div>
          {splitType === 'custom' && (
            <div className="form-row custom-split-row">
              <div className="form-group flex-1">
                <label className="form-label">{families.family1?.name} Share (₹)</label>
                <input name="family1Share" type="number" className="form-input" defaultValue={existingExpense?.family1Share || ''} min="0" step="0.01" placeholder="0.00" />
              </div>
              <div className="form-group flex-1">
                <label className="form-label">{families.family2?.name} Share (₹)</label>
                <input name="family2Share" type="number" className="form-input" defaultValue={existingExpense?.family2Share || ''} min="0" step="0.01" placeholder="0.00" />
              </div>
            </div>
          )}
          {splitType === 'weighted' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
              <div style={{ width: '100%' }}><label className="form-label">Assign Shares (Adult=1, Child=0.5, None=0)</label></div>
              {allMembers.map(m => (
                <div key={m.id} className="form-group" style={{ flex: '1', minWidth: '120px', marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.7rem', textTransform: 'none' }}>{m.name} ({families[m.familyId]?.name})</label>
                  <input name={`weight_${m.id}`} type="number" className="form-input"
                    defaultValue={existingExpense?.memberWeights?.[m.id] ?? 1} min="0" step="0.1" placeholder="1" />
                </div>
              ))}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <textarea name="notes" className="form-input form-textarea" rows="2" placeholder="Any additional details..." defaultValue={existingExpense?.notes || ''} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={closeModal}>Cancel</button>
            <button type="submit" className="btn btn-primary">{existingExpense ? 'Update' : 'Add Expense'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
